import { format } from 'date-fns';

import { Button, buttonVariants } from '@/components/Buttons';
import HabitatIcon from '@/components/HabitatIcon';
import HabitatLink from '@/components/HabitatLink';
import { cn } from '@/lib/utils';
import { Video, VideoV2 } from '@/queries/media/types';
import { useSelector } from '@/redux/helper';
import { selectHasFullHighlights } from '@/redux/selectors/users';
import { trackMP } from '@/shared/mp';

interface HomeHighlightVideoInfoBarProps {
  highlight: Video | VideoV2;
  onNextHighlight?: () => void;
}

const HomeHighlightVideoInfoBar = ({ highlight, onNextHighlight }: HomeHighlightVideoInfoBarProps) => {
  const hasFullHighlights = useSelector(selectHasFullHighlights);
  const videoHabitat = 'habitat' in highlight ? highlight.habitat : (highlight as Video).habitatId;
  const type = 'videoType' in highlight ? highlight.videoType : highlight.type;
  const username = 'user' in highlight ? highlight.user?.username : (highlight as Video).username;
  const { publishedAt } = highlight;
  const habitat = useSelector((state) => state.allHabitats.find((h) => h._id === videoHabitat?._id));

  const trackVisitHabitatClick = () => {
    if (habitat) {
      trackMP('visit-habitat-link-clicked', {
        habitatId: habitat._id,
        entryPoint: 'highlight-info-bar',
      });
    }
  };

  return (
    <div className="relative z-10 bg-white shadow-[0px_4px_12px_0px_rgba(0,0,0,0.07)] mobile:px-[18px] mobile:py-[17px] desktop:flex desktop:items-center desktop:justify-between desktop:px-6 desktop:py-5">
      <div className="flex items-start gap-7 desktop:grow desktop:gap-0">
        {habitat?.zoo && (
          <div className="shrink-0 pb-4">
            <HabitatIcon
              variant="lg"
              habitatId={habitat._id}
              slug={habitat.slug}
              zooSlug={habitat.zoo.slug}
              profileImage={habitat.profileImage}
              title={habitat.title}
              zooLogo={habitat.zoo.logo}
              zooName={habitat.zoo.name}
              entryPoint="highlight-info-bar"
            />
          </div>
        )}
        <div className="flex min-w-0 grow flex-col desktop:px-5">
          {habitat?.zoo && (
            <HabitatLink
              habitatId={habitat._id}
              slug={habitat.slug}
              zooSlug={habitat.zoo.slug}
              className="preflight preflight-a text-xl/tight font-semibold text-grey-1 hover:text-grey-3"
              onClick={trackVisitHabitatClick}
            >
              {habitat.title}
            </HabitatLink>
          )}
          {habitat?.zoo && habitat.animal && (
            <p className="preflight preflight-p mt-1 text-xs font-semibold uppercase text-stone-500 desktop:text-[10px] desktop:leading-[11px]">
              {habitat.animal} AT {habitat.zoo.name}
            </p>
          )}
          <p className="preflight preflight-p desktop:texg-stone-500 mt-1 text-sm text-black desktop:text-base desktop:text-stone-500">
            <span className="line-clamp-1 desktop:inline" data-testid="highlight-title-info-bar-desktop">{`\u201C${
              highlight.title || 'Highlight'
            }\u201D`}</span>
            <span> taken by </span>
            {type === 'curated' ? (
              <span className="text-primary-green">@zoolife</span>
            ) : (
              <a
                href={`/u/${username || 'zoolife'}`}
                className="preflight preflight-a text-primary-green hover:text-secondary-green"
              >
                @{username || 'zoolife'}
              </a>
            )}
          </p>
          {publishedAt && (
            <div className="text-base text-stone-500 mobile:hidden">{format(new Date(publishedAt), 'd MMMM, haaa')}</div>
          )}
        </div>
      </div>
      <div className="flex shrink-0 gap-2 mobile:mt-3">
        {habitat?.zoo && (
          <HabitatLink
            habitatId={habitat._id}
            slug={habitat.slug}
            zooSlug={habitat.zoo.slug}
            className={cn(buttonVariants({ variant: 'default' }), 'flex-1')}
            onClick={trackVisitHabitatClick}
          >
            Visit Habitat
          </HabitatLink>
        )}
        {!(type === 'clip' && !hasFullHighlights) && onNextHighlight && (
          <Button variant="outline" onClick={onNextHighlight} className="flex-1">
            Next Clip
          </Button>
        )}
      </div>
    </div>
  );
};

export default HomeHighlightVideoInfoBar;
